/**
 * The pure half of the line sheet feedback store (KP-4572): item state,
 * revisions, merging with the server and the queue kept in this browser.
 *
 * Every write carries the full item and a revision (ms, monotonic per item);
 * the server applies it only when newer. So a retry, a double click or a late
 * flush can never undo a newer choice, and the queue needs no ids of its own.
 */

import { cleanBlock } from './feedbackText'

export const COMMENT_MAX = 1000
export const NOTE_MAX = 2000
export const MAX_BATCH_ITEMS = 50
// The server refuses bodies over 8 KiB; this leaves room for the envelope.
export const MAX_BATCH_BYTES = 7000
export const MAX_PENDING_ITEMS = 200

const PENDING_PREFIX = 'kp-ls-pending:'
export const pendingKey = token => `${PENDING_PREFIX}${token}`

export const BLANK_ITEM = Object.freeze({
  liked: false, interested: false, comment: '', rev: 0,
})

/** A revision newer than prev and not behind the clock. */
export const nextRev = (prev, now) => Math.max((Number(prev) || 0) + 1, now)

const isRev = rev => Number.isSafeInteger(rev) && rev >= 0

/** One item as this page keeps it, or null when the shape is wrong. */
export function toItem(raw) {
  if (!raw || typeof raw !== 'object' || !isRev(raw.rev)) return null
  return {
    liked: raw.liked === true,
    interested: raw.interested === true,
    comment: typeof raw.comment === 'string' ? raw.comment : '',
    rev: raw.rev,
  }
}

/** The server's reactions list as a map by product id. */
export function itemsFromReactions(reactions) {
  const list = Array.isArray(reactions) ? reactions : []
  return Object.fromEntries(list
    .filter(r => r && typeof r.product_id === 'string')
    .map(r => [r.product_id, toItem(r)])
    .filter(([, item]) => item))
}

/**
 * Server state and this browser's queue, merged by revision: the higher rev
 * wins, and a queued item the server already beats leaves the queue.
 */
export function mergeByRev(serverItems, pendingItems) {
  const winners = Object.entries(pendingItems).filter(([id, item]) => !serverItems[id] || item.rev > serverItems[id].rev)
  const pending = Object.fromEntries(winners)
  return { items: { ...serverItems, ...pending }, pending }
}

/** The note the server holds or the queued one, whichever is newer. */
export function mergeNote(serverNote, pendingNote) {
  if (pendingNote && pendingNote.rev > serverNote.rev) return { note: pendingNote, pending: pendingNote }
  return { note: serverNote, pending: null }
}

export function toNote(raw) {
  if (!raw || typeof raw !== 'object' || !isRev(raw.rev)) return { text: '', rev: 0 }
  return { text: typeof raw.text === 'string' ? raw.text : '', rev: raw.rev }
}

/** The body item the server expects, comment cleaned the way it will clean it. */
export const wireItem = (productId, item) => ({
  product_id: productId,
  liked: item.liked,
  interested: item.interested,
  comment: cleanBlock(item.comment, COMMENT_MAX),
  rev: item.rev,
})

const utf8Bytes = text => encodeURIComponent(text).replace(/%[0-9A-F]{2}/g, 'x').length

const fitsBatch = (batch, size, maxItems, maxBytes) => Boolean(batch)
  && batch.items.length < maxItems
  && batch.bytes + size <= maxBytes

/** Wire items cut into batches of at most 50 that each fit the server's body cap. */
export function batchItems(items, maxItems = MAX_BATCH_ITEMS, maxBytes = MAX_BATCH_BYTES) {
  return items.reduce((batches, item) => {
    const size = utf8Bytes(JSON.stringify(item)) + 1
    const last = batches[batches.length - 1]
    if (fitsBatch(last, size, maxItems, maxBytes)) {
      return [...batches.slice(0, -1), { items: [...last.items, item], bytes: last.bytes + size }]
    }
    return [...batches, { items: [item], bytes: size }]
  }, []).map(b => b.items)
}

/** At most MAX_PENDING_ITEMS, the most recent kept. */
export function capPending(items) {
  const entries = Object.entries(items)
  if (entries.length <= MAX_PENDING_ITEMS) return items
  return Object.fromEntries([...entries].sort((a, b) => b[1].rev - a[1].rev).slice(0, MAX_PENDING_ITEMS))
}

const isObject = value => Boolean(value) && typeof value === 'object'

function validItems(raw) {
  const items = isObject(raw) && isObject(raw.items) ? raw.items : {}
  const valid = Object.entries(items).map(([id, item]) => [id, toItem(item)]).filter(([, item]) => item)
  return capPending(Object.fromEntries(valid))
}

function validNote(raw) {
  const note = isObject(raw) && raw.note ? toNote(raw.note) : null
  return note && note.rev ? note : null
}

/** The queue this browser kept for the sheet. Never throws. */
export function loadPending(token) {
  try {
    const raw = JSON.parse(window.localStorage.getItem(pendingKey(token)) || 'null')
    return { items: validItems(raw), note: validNote(raw) }
  } catch (e) {
    return { items: {}, note: null }
  }
}

export function savePending(token, pending) {
  try {
    window.localStorage.setItem(pendingKey(token), JSON.stringify(pending))
  } catch (e) {
    // Blocked or full: the queue lives for this visit only.
  }
}
